// src/modules/production/components/ManufacturingForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Space,
  message,
  Spin,
  Tag,
  Typography,
  Alert,
  Table,
  Popconfirm,
} from 'antd';
import { PlusOutlined, ReloadOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient } from '@/services/api';

const { Option } = Select;
const { Text, Title } = Typography;

interface MenuItem {
  id: number;
  name: string;
  current_stock: number;
  stock_available: number;
  status: string;
  category: Array<{ name: string }>;
}

interface ProductionItem {
  key: string;
  menu_item_id: number;
  menu_item_name: string;
  quantity: number;
  current_stock: number;
  comment?: string;
}

interface ManufacturingFormProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ManufacturingForm: React.FC<ManufacturingFormProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [loadError, setLoadError] = useState<string>('');
  
  // Список позиций для добавления
  const [productionList, setProductionList] = useState<ProductionItem[]>([]);

  // Загрузка списка активных блюд
  const loadMenuItems = async () => {
    setLoadingItems(true);
    setLoadError('');
    
    try {
      const response = await apiClient.get('/api/admin/manufacturing/menu-items/');
      
      if (response.data.success) {
        const items = response.data.data.items || [];
        setMenuItems(items);
        
        if (items.length === 0) {
          setLoadError('В системе нет активных блюд');
          message.warning('Не найдено ни одного активного блюда');
        }
      } else {
        setLoadError('Ошибка: ' + (response.data.message || 'Неизвестная ошибка'));
        message.error('Не удалось загрузить список блюд');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Неизвестная ошибка';
      setLoadError(`Ошибка загрузки: ${errorMsg}`);
      message.error('Не удалось загрузить список блюд: ' + errorMsg);
    } finally {
      setLoadingItems(false);
    }
  };

  // Загружаем блюда при открытии формы
  useEffect(() => {
    if (visible) {
      loadMenuItems();
    } else {
      // Сбрасываем при закрытии
      setMenuItems([]);
      setSelectedItem(null);
      setLoadError('');
      setProductionList([]);
      form.resetFields();
    }
  }, [visible]);

  // Обработка выбора блюда
  const handleMenuItemSelect = (menuItemId: number) => {
    const item = menuItems.find(i => i.id === menuItemId);
    setSelectedItem(item || null);
  };

  // Добавить позицию в список
  const handleAddToList = async () => {
    try {
      const values = await form.validateFields(['menu_item', 'quantity_added', 'comment']);
      
      if (!selectedItem) {
        message.error('Выберите блюдо');
        return;
      }

      // Проверяем, не добавлено ли уже это блюдо
      const existingItem = productionList.find(item => item.menu_item_id === values.menu_item);
      if (existingItem) {
        message.warning('Это блюдо уже добавлено в список. Удалите его или измените количество.');
        return;
      }

      const newItem: ProductionItem = {
        key: `${values.menu_item}-${Date.now()}`,
        menu_item_id: values.menu_item,
        menu_item_name: selectedItem.name,
        quantity: values.quantity_added,
        current_stock: selectedItem.current_stock,
        comment: values.comment || '',
      };

      setProductionList([...productionList, newItem]);
      
      // Очищаем форму для следующей позиции
      form.resetFields(['menu_item', 'quantity_added', 'comment']);
      setSelectedItem(null);
      
      message.success(`${selectedItem.name} добавлено в список`);
    } catch (error) {
      // Валидация не прошла
    }
  };

  // Удалить позицию из списка
  const handleRemoveFromList = (key: string) => {
    setProductionList(productionList.filter(item => item.key !== key));
    message.success('Позиция удалена из списка');
  };

  // Отправка всего списка (оптимизированная версия - один запрос)
  const handleSubmitAll = async () => {
    if (productionList.length === 0) {
      message.warning('Добавьте хотя бы одну позицию в список');
      return;
    }

    setLoading(true);

    try {
      // Формируем массив для отправки
      const items = productionList.map(item => ({
        menu_item: item.menu_item_id,
        quantity_added: item.quantity,
        comment: item.comment || '',
      }));

      // Отправляем одним запросом
      const response = await apiClient.post('/api/admin/manufacturing/logs/bulk-create/', {
        items
      });

      const data = response.data.data;
      const successCount = data.success_count;
      const errorCount = data.error_count;
      const errors = data.errors || [];

      // Показываем результат
      if (successCount > 0 && errorCount === 0) {
        message.success(`Успешно добавлено ${successCount} записей о производстве`);
        setProductionList([]);
        form.resetFields();
        onSuccess();
        onClose();
      } else if (successCount > 0 && errorCount > 0) {
        message.warning(`Добавлено ${successCount} записей, ${errorCount} с ошибками`);
        
        // Формируем список ошибок
        const errorMessages = errors.map((err: any) => {
          const item = productionList[err.index];
          return `${item?.menu_item_name || `Позиция ${err.index + 1}`}: ${err.error}`;
        });
        
        // Показываем ошибки
        Modal.error({
          title: 'Некоторые позиции не удалось добавить',
          content: (
            <div>
              {errorMessages.map((err: string, idx: number) => (
                <div key={idx}>• {err}</div>
              ))}
            </div>
          ),
        });
        
        // Удаляем только успешно добавленные позиции
        const errorIndices = errors.map((err: any) => err.index);
        setProductionList(productionList.filter((_, index) => errorIndices.includes(index)));
        
        onSuccess();
      } else {
        message.error('Не удалось добавить ни одной записи');
        
        const errorMessages = errors.map((err: any) => {
          const item = productionList[err.index];
          return `${item?.menu_item_name || `Позиция ${err.index + 1}`}: ${err.error}`;
        });
        
        Modal.error({
          title: 'Ошибки при добавлении',
          content: (
            <div>
              {errorMessages.map((err: string, idx: number) => (
                <div key={idx}>• {err}</div>
              ))}
            </div>
          ),
        });
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Неизвестная ошибка';
      message.error(`Ошибка: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Сброс формы при закрытии
  const handleCancel = () => {
    form.resetFields();
    setSelectedItem(null);
    setProductionList([]);
    onClose();
  };

  // Колонки таблицы
  const columns: ColumnsType<ProductionItem> = [
    {
      title: 'Блюдо',
      dataIndex: 'menu_item_name',
      key: 'menu_item_name',
      width: '40%',
    },
    {
      title: 'Текущий остаток',
      dataIndex: 'current_stock',
      key: 'current_stock',
      width: '15%',
      align: 'center',
      render: (value: number) => `${value} шт.`,
    },
    {
      title: 'Добавить',
      dataIndex: 'quantity',
      key: 'quantity',
      width: '15%',
      align: 'center',
      render: (value: number) => {
        const isPositive = value > 0;
        return (
          <Tag 
            color={isPositive ? "success" : "error"} 
            className="font-bold"
          >
            {isPositive ? `+${value}` : value} шт.
          </Tag>
        );
      },
    },
    {
      title: 'Комментарий',
      dataIndex: 'comment',
      key: 'comment',
      width: '20%',
      ellipsis: true,
      render: (value: string) => value || <Text type="secondary" italic>—</Text>,
    },
    {
      title: 'Действие',
      key: 'action',
      width: '10%',
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="Удалить позицию?"
          onConfirm={() => handleRemoveFromList(record.key)}
          okText="Да"
          cancelText="Нет"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title="Добавить записи о производстве"
      open={visible}
      onCancel={handleCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Отмена
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmitAll}
          loading={loading}
          icon={<SaveOutlined />}
          disabled={productionList.length === 0}
        >
          Сохранить все ({productionList.length})
        </Button>,
      ]}
    >
      {/* Ошибка загрузки */}
      {loadError && (
        <Alert
          message="Ошибка загрузки блюд"
          description={
            <div>
              <p>{loadError}</p>
              <Button 
                type="link" 
                icon={<ReloadOutlined />}
                onClick={loadMenuItems}
                size="small"
              >
                Попробовать снова
              </Button>
            </div>
          }
          type="error"
          showIcon
          className="mb-4"
        />
      )}

      {/* Форма добавления позиции */}
      <div className="mb-6 p-4 bg-gray-50 rounded border">
        <Title level={5} className="mt-0">Добавить позицию</Title>
          <Alert
            message="💡 Подсказка"
            description={
              <div>
                <div>• <strong>Положительное число (+10)</strong> — производство блюд</div>
                <div>• <strong>Отрицательное число (-5)</strong> — списание (брак, ревизия, испорчено)</div>
              </div>
            }
            type="info"
            showIcon
            closable
            className="mb-3"
          />
        
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <div className="grid grid-cols-3 gap-4">
            {/* Выбор блюда */}
            <Form.Item
              label="Блюдо"
              name="menu_item"
              rules={[{ required: true, message: 'Выберите блюдо' }]}
              className="mb-0"
            >
              <Select
                placeholder="Выберите блюдо"
                showSearch
                loading={loadingItems}
                optionFilterProp="children"
                onChange={handleMenuItemSelect}
                notFoundContent={
                  loadingItems ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin size="small" />
                      <div style={{ marginTop: 8 }}>Загрузка...</div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div>Нет блюд</div>
                      <Button 
                        type="link" 
                        icon={<ReloadOutlined />}
                        onClick={loadMenuItems}
                        size="small"
                      >
                        Обновить
                      </Button>
                    </div>
                  )
                }
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {menuItems.map(item => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* Количество */}
            <Form.Item
            label="Количество"
            name="quantity_added"
            tooltip="Положительное число — производство, отрицательное — списание"
            rules={[
              { required: true, message: 'Укажите количество' },
              { 
                type: 'number',
                message: 'Должно быть числом'
              },
              { 
                validator: (_, value) => {
                  if (value === 0) {
                    return Promise.reject(new Error('Количество не может быть 0'));
                  }
                  if (value > 1000) {
                    return Promise.reject(new Error('Максимум 1000 шт. для производства'));
                  }
                  if (value < -1000) {
                    return Promise.reject(new Error('Максимум -1000 шт. для списания'));
                  }
                  // Проверка при списании - хватает ли на складе
                  if (value < 0 && selectedItem) {
                    const absValue = Math.abs(value);
                    if (absValue > selectedItem.current_stock) {
                      return Promise.reject(
                        new Error(
                          `Недостаточно на складе. Вы пытаетесь списать ${absValue} шт., но на складе только ${selectedItem.current_stock} шт.`
                        )
                      );
                    }
                  }
                  return Promise.resolve();
                }
              },
            ]}
            className="mb-0"
          >
            <InputNumber
              placeholder="Кол-во (+производство / -списание)"
              style={{ width: '100%' }}
              min={-1000}
              max={1000}
              addonAfter="шт."
              precision={0}
            />
          </Form.Item>

            {/* Комментарий */}
            <Form.Item
              label="Комментарий (необязательно)"
              name="comment"
              className="mb-0"
            >
              <Input
                placeholder="Комментарий"
                maxLength={100}
              />
            </Form.Item>
          </div>

          {/* Информация о выбранном блюде */}
          {selectedItem && (
            <div className="mt-3 p-2 bg-white rounded border">
              <Space size="large">
                <Text type="secondary">
                  Текущий остаток: <Text strong>{selectedItem.current_stock} шт.</Text>
                </Text>
                <Text type="secondary">
                  Доступно: <Text strong>{selectedItem.stock_available} шт.</Text>
                </Text>
              </Space>
            </div>
          )}

          {/* Кнопка добавления в список */}
          <div className="mt-4">
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddToList}
              block
              disabled={menuItems.length === 0}
            >
              Добавить в список
            </Button>
          </div>
        </Form>
      </div>

      {/* Таблица добавленных позиций */}
      {productionList.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <Title level={5} className="m-0">
              Список для добавления ({productionList.length})
            </Title>
            <Button
              size="small"
              danger
              onClick={() => setProductionList([])}
            >
              Очистить всё
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={productionList}
            pagination={false}
            size="small"
            bordered
            scroll={{ y: 300 }}
          />
        </div>
      )}

      {/* Подсказка */}
      {productionList.length === 0 && (
        <Alert
          message="Добавьте позиции в список"
          description="Выберите блюдо, укажите количество и нажмите 'Добавить в список'. Затем сохраните всё одной кнопкой."
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};